import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox as BrCheckbox, Fieldset, Layout } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { MaskedTextFormField, masks } from '../../../../../components/FormFields/MaskedTextFormField';
import { NumericFormField } from '../../../../../components/FormFields/NumericFormField';
import { TextFormField } from '../../../../../components/FormFields/TextFormField';
import { VehicleType } from '../../../../../resources/base/models/vehicleTypes';
import { isEngineElectric, isEngineElectricOrHybrid } from '../../../../../services/common/miscUtils';
import { VehicleConfigData } from '../../../../../types';
import { Datepicker, Dropdown, LicensePlateInputField } from '../../fieldset';
import {
  getEngineList,
  getSortedManufacturers,
  getVehicleList,
  modelYearList,
  shouldShowUsedDemoCarModal,
} from './utils';

export const UsedVehicleBaseForm: FunctionComponent<{
  manufacturersList: {
    value: string;
    label: string;
  }[];
  allDataForAllVehicles: Array<VehicleIntegration.Components.Schemas.CatalogVehicleDataResponse>;
  vehicleGroupModelsList: {
    value: string;
    label: string;
    id: string;
  }[];
  isFormReadonly: boolean;
  setShowDemoCarModal: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ manufacturersList, allDataForAllVehicles, vehicleGroupModelsList, isFormReadonly, setShowDemoCarModal }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched } = useFormikContext<VehicleConfigData>();
  const [isDropdownDisabled, setIsDropdownDisabled] = useState(true);

  useEffect(() => {
    if (manufacturersList.length === 0) {
      return;
    }

    if (manufacturersList.length === 1) {
      setFieldValue('brand', manufacturersList[0].value);
      setIsDropdownDisabled(true);
    } else {
      setIsDropdownDisabled(false);
    }
  }, [isDropdownDisabled, manufacturersList, setFieldValue]);

  useEffect(() => {
    const modelYears = modelYearList(allDataForAllVehicles);
    const engines = getEngineList(values?.model, allDataForAllVehicles);
    const vehicles = getVehicleList(values?.model, values?.engine, allDataForAllVehicles);
    if (modelYears.length === 1) {
      setFieldValue('model', modelYears[0].value);
    }

    if (engines.length === 1) {
      setFieldValue('engine', engines[0].value);
    }

    if (vehicles.length === 1) {
      setFieldValue('vehicleMilesId', vehicles[0].value);
      const modelNameWithoutPrice = vehicles[0].label.split(' [')[0];
      setFieldValue('modelName', modelNameWithoutPrice);
      setFieldValue('modelDescription', modelNameWithoutPrice);
    }
  }, [allDataForAllVehicles, setFieldValue, values?.engine, values?.model]);

  useEffect(() => {
    if (vehicleGroupModelsList.length === 1) {
      setFieldValue('modelGroup', vehicleGroupModelsList[0].value);
    }
  }, [setFieldValue, vehicleGroupModelsList]);

  return (
    <Fieldset>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/4" s="1/1">
            <Dropdown
              name="brand"
              label={t('step1:configVehicle:brand')}
              list={getSortedManufacturers(manufacturersList)}
              noPlaceholder={false}
              carTypeStep1={VehicleType.USED}
              disabled={isFormReadonly || isDropdownDisabled}
            />
          </Layout.Item>

          <Layout.Item default="1/4" s="1/1">
            <Dropdown
              name="modelGroup"
              label={t('step1:configVehicle:modelGroup')}
              list={vehicleGroupModelsList}
              noPlaceholder={false}
              disabled={isFormReadonly}
              onValueChange={() => {
                setFieldValue('vehicleMilesId', '');
                setFieldValue('modelDescription', '');
                setFieldTouched('vehicleMilesId', false);
                setFieldTouched('modelDescription', false);
              }}
            />
          </Layout.Item>

          <Layout.Item default="1/4" s="1/1">
            <Dropdown
              name="model"
              label={t('step1:configVehicle:model')}
              list={modelYearList(allDataForAllVehicles)}
              noPlaceholder={false}
              disabled={isFormReadonly}
            />
          </Layout.Item>

          <Layout.Item default="1/4" s="1/1">
            <Dropdown
              name="engine"
              label={t('step1:configVehicle:engine')}
              list={getEngineList(values?.model, allDataForAllVehicles)}
              noPlaceholder={false}
              disabled={isFormReadonly}
            />
          </Layout.Item>

          <Layout.Item default="1/2" s="1/1">
            <Dropdown
              name="vehicleMilesId"
              label={t('step1:configVehicle:modelName')}
              list={getVehicleList(values?.model, values?.engine, allDataForAllVehicles)}
              noPlaceholder={false}
              disabled={isFormReadonly}
              onValueChange={(label: string) => {
                setFieldValue('modelName', label);
                setFieldValue('modelDescription', label);
              }}
            />
          </Layout.Item>

          <Layout.Item default="1/2" s="1/1">
            <TextFormField
              fieldName="modelDescription"
              labelTranslationKey="step1:configVehicle:modelDescription"
              isDisabled={isFormReadonly}
              infoTextTranslationKey={t('step1:configVehicle:modelDescriptionTooltip')}
            />
          </Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/4" s="1/1">
            <LicensePlateInputField
              name="licensePlate"
              label={t('step1:configVehicle:licensePlate')}
              notRequired
              setFieldValue={setFieldValue}
              value={values.licensePlate}
              disabled={isFormReadonly}
            />
          </Layout.Item>

          <Layout.Item default="1/4" s="1/1">
            <MaskedTextFormField
              mask={masks.vinMask}
              fieldName="vin"
              labelTranslationKey="step1:configVehicle:vin"
              isDisabled={isFormReadonly}
              overwrite
              removeWhitespace
            />
          </Layout.Item>

          <Layout.Item default="1/4" s="1/1">
            <NumericFormField
              fieldName="batteryPrice"
              type="money"
              maximumFractionDigits={2}
              labelTranslationKey="step1:configVehicle:batteryPrice"
              optional={!isEngineElectricOrHybrid(values?.engine)}
              isDisabled={!isEngineElectricOrHybrid(values?.engine) || isFormReadonly}
            />
          </Layout.Item>

          <Layout.Item default="1/4" s="1/1">
            <NumericFormField
              fieldName="co2emision"
              labelTranslationKey={t('step1:configVehicle:co2emission')}
              type="emission"
              isDisabled={isEngineElectric(values?.engine) || isFormReadonly}
              maximumFractionDigits={0}
            />
          </Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/3" s="1/1">
            <BrCheckbox
              checked={values?.isDemo}
              name="demoCar"
              disabled={isFormReadonly}
              onChange={(e) => {
                setFieldValue('isDemo', e.target.checked);
                shouldShowUsedDemoCarModal(
                  values?.regDate ?? '',
                  values.currMileage,
                  setShowDemoCarModal,
                  !!e.target.checked
                );
              }}
            >
              {t('step1:configVehicle:demoCarCheckbox')}
            </BrCheckbox>
          </Layout.Item>

          <Layout.Item default="1/3" s="1/1">
            <Datepicker
              name="regDate"
              label={t('step1:configVehicle:regDate')}
              infoText={t('step1:dateInfoText:label')}
              value={values.regDate}
              setFieldValue={setFieldValue}
              maxDate={new Date().setHours(0, 0, 0, 0)}
              disabled={isFormReadonly}
              onValueChange={(regDate: string) =>
                shouldShowUsedDemoCarModal(regDate, values.currMileage, setShowDemoCarModal, !!values.isDemo)
              }
            />
          </Layout.Item>
          <Layout.Item default="1/3" s="1/1">
            <NumericFormField
              fieldName="currMileage"
              labelTranslationKey={t('step1:configVehicle:currMileage')}
              type="km"
              maximumFractionDigits={0}
              isDisabled={isFormReadonly}
              onValueChange={(currMileage: number) =>
                shouldShowUsedDemoCarModal(values?.regDate ?? '', currMileage, setShowDemoCarModal, !!values.isDemo)
              }
              allowEmpty
            />
          </Layout.Item>
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  );
};
